import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Target, Home, BookOpen, BarChart3, User, LogOut, Menu, X,
  Mail, Award, Settings, TrendingUp, TrendingDown, Minus, Trophy, ClipboardCheck, Flag,
  Download, Trash2, Sparkles, MessageSquare, Info, Smartphone, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = `${window.location.origin}/api`;

const CATEGORY_LABELS = {
  putting: "Putting",
  chipping: "Chipping", 
  pitching: "Pitching",
  iron_play: "Iron Play",
  driving: "Driving",
  course_management: "Course Mgmt"
};

const CATEGORY_COLORS = {
  putting: "bg-green-500",
  chipping: "bg-yellow-500",
  pitching: "bg-orange-500",
  iron_play: "bg-blue-500",
  driving: "bg-purple-500",
  course_management: "bg-pink-500"
};

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingHandicap, setEditingHandicap] = useState(false);
  const [handicapValue, setHandicapValue] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, profileRes, ratingsRes] = await Promise.all([
        authFetch(`${API}/auth/me`),
        authFetch(`${API}/profile`),
        authFetch(`${API}/ratings`)
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
      }

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
      }

      if (ratingsRes.ok) {
        const ratingsData = await ratingsRes.json();
        setRatings(ratingsData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST" });
      clearToken();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleUpdateOnboarding = () => {
    navigate("/onboarding");
    toast.info("You can update your profile information");
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: "Dashboard", path: "/dashboard" },
    { icon: <User className="w-5 h-5" />, label: "Profile", path: "/profile", active: true },
  ];

  const getSkillLevelBadge = (level) => {
    const variants = {
      beginner: "bg-green-500/10 text-green-700 border-green-500/20",
      intermediate: "bg-blue-500/10 text-blue-700 border-blue-500/20",
      advanced: "bg-purple-500/10 text-purple-700 border-purple-500/20"
    };
    return variants[level] || variants.beginner;
  };

  const getRatingTrend = (category) => {
    if (!ratings?.rating_history || ratings.rating_history.length < 2) return null;
    const history = ratings.rating_history;
    const current = history[history.length - 1]?.ratings?.[category] || 50;
    const previous = history[history.length - 2]?.ratings?.[category] || 50;
    const diff = current - previous;
    if (diff > 0) return { icon: <TrendingUp className="w-4 h-4 text-green-500" />, text: `+${diff.toFixed(1)}` };
    if (diff < 0) return { icon: <TrendingDown className="w-4 h-4 text-red-500" />, text: diff.toFixed(1) };
    return { icon: <Minus className="w-4 h-4 text-muted-foreground" />, text: "0" };
  };

  const getImprovementColor = (improvement) => {
    if (improvement >= 10) return "text-green-500";
    if (improvement >= 5) return "text-emerald-500";
    if (improvement > 0) return "text-lime-500";
    if (improvement < 0) return "text-red-500";
    return "text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-card border-b border-border px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <Target className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-heading text-lg font-semibold">GolfFlowPro</span>
        </div>
        <Button 
          data-testid="mobile-menu-btn"
          variant="ghost" 
          size="icon" 
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-40 transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Target className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading text-xl font-semibold">GolfFlowPro</span>
          </div>

          <nav className="space-y-2">
            {navItems.map((item, index) => (
              <button
                key={index}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm transition-colors ${
                  item.active 
                    ? "bg-primary text-primary-foreground" 
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <Button 
            data-testid="logout-btn"
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
              Profile & Ratings
            </h1>
            <p className="text-muted-foreground">
              Track your skill ratings and improvement across all areas.
            </p>
          </motion.div>

          <div className="space-y-6">
            {/* User Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card data-testid="user-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
                        <span className="font-heading text-3xl font-bold text-primary-foreground">
                          {user?.name?.charAt(0) || "U"}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h2 className="font-heading text-2xl font-semibold text-foreground">
                        {user?.name}
                      </h2>
                      <div className="flex items-center gap-2 text-muted-foreground mt-1">
                        <Mail className="w-4 h-4" />
                        {user?.email}
                      </div>
                      {profile?.skill_level && (
                        <Badge className={`mt-2 capitalize ${getSkillLevelBadge(profile.skill_level)}`}>
                          <Award className="w-3 h-3 mr-1" />
                          {profile.skill_level} Golfer
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Overall Rating Card */}
            {ratings && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Card data-testid="overall-rating-card" className="bg-primary text-primary-foreground">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-primary-foreground/70 text-sm font-medium mb-1">Overall Rating</p>
                        <h2 className="font-heading text-5xl font-bold">
                          {ratings.overall_rating?.toFixed(1) || "50.0"}
                        </h2>
                        <p className="text-primary-foreground/70 text-sm">out of 100</p>
                      </div>
                      <div className="text-right">
                        <p className="text-primary-foreground/70 text-sm font-medium mb-1">Total Improvement</p>
                        <h3 className={`font-heading text-3xl font-bold ${ratings.total_improvement >= 0 ? 'text-accent' : 'text-red-300'}`}>
                          {ratings.total_improvement >= 0 ? '+' : ''}{ratings.total_improvement?.toFixed(1) || "0"}%
                        </h3>
                        <p className="text-primary-foreground/70 text-sm">since you started</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Skill Ratings Grid */}
            {ratings?.current_ratings && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card data-testid="skill-ratings-card">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      Skill Ratings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(ratings.current_ratings).map(([category, rating]) => {
                        const trend = getRatingTrend(category);
                        const initial = ratings.initial_ratings?.[category] || 50;
                        const improvement = initial > 0 ? ((rating - initial) / initial * 100) : 0;
                        
                        return (
                          <div key={category} data-testid={`rating-${category}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS[category]}`} />
                                <span className="font-medium text-foreground">
                                  {CATEGORY_LABELS[category]}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                {trend && (
                                  <span className="flex items-center gap-1 text-xs">
                                    {trend.icon}
                                    <span className="text-muted-foreground">{trend.text}</span>
                                  </span>
                                )}
                                <span className={`text-sm font-medium ${getImprovementColor(improvement)}`}>
                                  {improvement >= 0 ? '+' : ''}{improvement.toFixed(1)}%
                                </span>
                                <span className="font-heading text-lg font-bold w-12 text-right">
                                  {rating.toFixed(0)}
                                </span>
                              </div>
                            </div>
                            <Progress 
                              value={rating} 
                              className="h-2"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Golf Profile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card data-testid="golf-profile-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="font-heading text-xl">Golf Profile</CardTitle>
                    {user?.is_beta_user && (
                      <Badge className="bg-gray-100 text-[#555] border-gray-300 text-xs" data-testid="beta-badge">Beta Tester</Badge>
                    )}
                  </div>
                  <Button 
                    data-testid="edit-profile-btn"
                    variant="outline" 
                    size="sm"
                    onClick={handleUpdateOnboarding}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Handicap</p>
                      {editingHandicap ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="54"
                            value={handicapValue}
                            onChange={(e) => setHandicapValue(e.target.value)}
                            className="w-20 bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-[#111] text-lg font-semibold focus:outline-none focus:border-emerald-500"
                            data-testid="handicap-edit-input"
                            autoFocus
                          />
                          <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-[#111] h-8 px-2"
                            data-testid="handicap-save-btn"
                            onClick={async () => {
                              try {
                                const res = await authFetch(`${API}/settings/handicap`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ handicap: handicapValue ? parseFloat(handicapValue) : null })
                                });
                                if (res.ok) {
                                  setProfile(prev => ({ ...prev, handicap: handicapValue ? parseFloat(handicapValue) : null }));
                                  toast.success("Handicap updated");
                                }
                              } catch (e) { toast.error("Failed to save"); }
                              setEditingHandicap(false);
                            }}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-[#555]"
                            onClick={() => setEditingHandicap(false)}>Cancel</Button>
                        </div>
                      ) : (
                        <p className="font-semibold text-lg cursor-pointer hover:text-emerald-400 transition-colors"
                          data-testid="handicap-value"
                          onClick={() => { setHandicapValue(profile?.handicap ?? ""); setEditingHandicap(true); }}>
                          {profile?.handicap ?? "Not set"} <Settings className="w-3 h-3 inline ml-1 text-[#555]" />
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Age</p>
                      <p className="font-semibold text-lg">
                        {profile?.age ?? "Not set"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Driving Distance</p>
                      <p className="font-semibold text-lg">
                        {profile?.driving_distance ? `${profile.driving_distance} yds` : "Not set"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Practice/Week</p>
                      <p className="font-semibold text-lg">
                        {profile?.practice_hours_per_week ? `${profile.practice_hours_per_week} hrs` : "Not set"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Coach Mode */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}>
              <Card data-testid="coach-mode-card">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center">
                        <ClipboardCheck className="w-5 h-5 text-blue-700" />
                      </div>
                      <div>
                        <p className="text-[#111] font-semibold">Coach Mode</p>
                        <p className="text-[#555] text-xs">Create drills and manage players</p>
                      </div>
                    </div>
                    {user?.role === "coach" ? (
                      <Button size="sm" onClick={() => navigate("/coach")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="go-to-coach-btn">
                        Open Coach Hub
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" data-testid="enable-coach-btn" onClick={async () => {
                        try {
                          const res = await authFetch(`${API}/api/coach/become-coach`, { method: "POST" });
                          if (res.ok) {
                            toast.success("Coach mode enabled!");
                            fetchData();
                          }
                        } catch (e) { toast.error("Failed to enable coach mode"); }
                      }}>
                        Enable
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Goals */}
            {profile?.goals?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <Card data-testid="goals-card">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Goals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.goals.map((goal, index) => (
                        <Badge key={index} variant="secondary" className="capitalize py-1.5 px-3">
                          {goal.replace("_", " ")}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Weak Areas */}
            {profile?.weak_areas?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card data-testid="weak-areas-card">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Focus Areas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.weak_areas.map((area, index) => (
                        <Badge key={index} variant="outline" className="capitalize py-1.5 px-3">
                          {area.replace("_", " ")}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Personalisation Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.42 }}
            >
              <Card className="border-border/40">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-[#555]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground">Personalised Plans</h3>
                      <p className="text-sm text-muted-foreground">Adapts missions based on your feedback</p>
                    </div>
                  </div>
                  <button
                    data-testid="personalisation-toggle"
                    onClick={async () => {
                      const current = user?.personalisation_enabled !== false;
                      try {
                        const res = await authFetch(`${API}/settings/personalisation`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ enabled: !current })
                        });
                        if (res.ok) {
                          setUser(prev => ({ ...prev, personalisation_enabled: !current }));
                          toast.success(!current ? "Personalisation enabled" : "Personalisation disabled");
                        }
                      } catch (e) { toast.error("Failed to update"); }
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      user?.personalisation_enabled !== false ? "bg-emerald-500" : "bg-gray-600"
                    }`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      user?.personalisation_enabled !== false ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Feedback Cadence */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.42 }}
            >
              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground">Feedback Level</h3>
                      <p className="text-sm text-muted-foreground">How often you give feedback during practice</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { value: "minimal", label: "Minimal", desc: "End of session only" },
                      { value: "standard", label: "Standard", desc: "End of each block" },
                      { value: "detailed", label: "Detailed", desc: "Key drills only" }
                    ].map(opt => (
                      <button key={opt.value}
                        data-testid={`cadence-${opt.value}`}
                        onClick={async () => {
                          try {
                            const res = await authFetch(`${API}/settings/feedback-cadence`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ cadence: opt.value })
                            });
                            if (res.ok) {
                              setUser(prev => ({ ...prev, feedback_cadence: opt.value }));
                              toast.success(`Feedback set to ${opt.label}`);
                            }
                          } catch (e) { toast.error("Failed to update"); }
                        }}
                        className={`flex-1 p-3 rounded-xl border text-center transition-all ${
                          (user?.feedback_cadence || "standard") === opt.value
                            ? "bg-blue-500/15 border-blue-500/40"
                            : "bg-background border-border/40 hover:bg-accent/50"
                        }`}
                      >
                        <p className={`text-sm font-medium ${(user?.feedback_cadence || "standard") === opt.value ? "text-blue-400" : "text-foreground"}`}>{opt.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>


            {/* App Version + Cache + Device Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.44 }}
            >
              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gray-500/10 flex items-center justify-center">
                      <Info className="w-5 h-5 text-[#555]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground">App Info</h3>
                      <p className="text-sm text-muted-foreground">Version & device details</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-white/5 border border-gray-200">
                      <p className="text-xs text-[#555] mb-0.5">Build Version</p>
                      <p className="text-sm font-mono font-medium text-[#111]" data-testid="build-version-value">
                        {window.__GFP_VERSION || "unknown"}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5 border border-gray-200">
                      <p className="text-xs text-[#555] mb-0.5">Browser</p>
                      <p className="text-sm font-medium text-[#111]" data-testid="browser-value">
                        {(() => {
                          const ua = navigator.userAgent || "";
                          if (/CriOS/.test(ua)) return "Chrome (iOS)";
                          if (/Safari/.test(ua) && !/Chrome/.test(ua) && /iPad|iPhone|iPod/.test(ua)) return "Safari (iOS)";
                          if (/Safari/.test(ua) && !/Chrome/.test(ua)) return "Safari";
                          if (/Chrome/.test(ua)) return "Chrome";
                          if (/Firefox|FxiOS/.test(ua)) return "Firefox";
                          return "Other";
                        })()}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5 border border-gray-200">
                      <p className="text-xs text-[#555] mb-0.5">Device</p>
                      <p className="text-sm font-medium text-[#111]" data-testid="device-value">
                        {/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ? "iOS" : /Android/.test(navigator.userAgent) ? "Android" : "Desktop"}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5 border border-gray-200">
                      <p className="text-xs text-[#555] mb-0.5">Mode</p>
                      <p className="text-sm font-medium text-[#111]" data-testid="mode-value">
                        {window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true ? "PWA (Installed)" : "Browser"}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" data-testid="hard-refresh-btn"
                      onClick={() => {
                        if ('caches' in window) {
                          caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
                        }
                        if ('serviceWorker' in navigator) {
                          navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
                        }
                        setTimeout(() => window.location.reload(true), 200);
                      }}
                      className="text-sm">
                      Clear Cache & Refresh
                    </Button>
                    <Button variant="outline" size="sm" data-testid="force-sw-update-btn"
                      onClick={() => {
                        if ('serviceWorker' in navigator) {
                          navigator.serviceWorker.ready.then(reg => {
                            reg.update().then(() => toast.success("Checked for updates")).catch(() => toast.error("Update check failed"));
                          }).catch(() => toast.error("Service worker not ready"));
                        } else {
                          toast.info("No service worker detected");
                        }
                      }}
                      className="text-sm">
                      Check for Updates
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>


            {/* Coach Mode */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
            >
              {user?.role === "coach" ? (
                <Card className="border-purple-500/20 bg-purple-500/5">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                        <ClipboardCheck className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-foreground">Coach Dashboard</h3>
                        <p className="text-sm text-muted-foreground">Manage your students and drills</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/coach")} data-testid="go-coach-dashboard-btn">
                      Open →
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-emerald-500/20">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Flag className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-foreground">Are You a Coach?</h3>
                        <p className="text-sm text-muted-foreground">Manage students, assign drills, track progress</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => {
                      try {
                        const res = await authFetch(`${API}/coach/become-coach`, { method: "POST" });
                        if (res.ok) {
                          toast.success("You are now a coach!");
                          navigate("/coach");
                        }
                      } catch (e) { toast.error("Failed to upgrade"); }
                    }} data-testid="become-coach-btn">
                      Activate →
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {/* Data Management */}
              <Card className="border-border/40 mb-4">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-foreground">Your Data</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button
                    data-testid="export-data-btn"
                    variant="outline"
                    className="border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10"
                    onClick={async () => {
                      try {
                        const res = await authFetch(`${API}/data/export`);
                        if (res.ok) {
                          const data = await res.json();
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "golfflowpro-my-data.json";
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Data exported successfully");
                        }
                      } catch (e) { toast.error("Export failed"); }
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export My Data
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-destructive/20">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-destructive">Danger Zone</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-3">
                    {(user?.is_beta_user || profile?.is_beta_user) && (
                      <Button
                        data-testid="reset-to-start-btn"
                        variant="outline"
                        className="text-[#555] border-gray-300 hover:bg-gray-100"
                        onClick={async () => {
                          if (!window.confirm("Reset your account to the start? This clears your onboarding, sessions, and weekly plan so you can test all flows from scratch. Your login and password stay the same.")) return;
                          try {
                            const res = await authFetch(`${API}/tester/reset-my-account`, { method: "POST" });
                            if (res.ok) {
                              toast.success("Account reset! Redirecting to onboarding...");
                              setTimeout(() => { window.location.href = "/"; }, 1000);
                            } else { toast.error("Reset failed"); }
                          } catch (e) { toast.error("Reset failed"); }
                        }}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset to Start
                      </Button>
                    )}
                    <Button 
                      data-testid="logout-main-btn"
                      variant="outline" 
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                    <Button
                      data-testid="delete-account-btn"
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={async () => {
                        if (!window.confirm("Are you sure you want to permanently delete your account and all data? This action cannot be undone.")) return;
                        if (!window.confirm("This is irreversible. All your practice history, plans, and drill data will be permanently removed. Continue?")) return;
                        try {
                          const res = await authFetch(`${API}/account/delete`, { method: "POST" });
                          if (res.ok) {
                            toast.success("Account deleted");
                            navigate("/");
                          }
                        } catch (e) { toast.error("Deletion failed"); }
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Deleting your account permanently removes all data. This cannot be undone.</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
